import { construireCsv } from '../api/csv'
import { dateLongue, couleurOrganisation, fcfa, initiales, nombre } from '../api/format'
import type { DonneesPlateforme } from '../api/types'
import { classeAccesApi, libelleAccesApi } from '../api/accesApi'
import { Badge, BadgePalier, palierDiverge, statutDe } from './Badges'
import { Icone } from './Icone'

/** Télécharge le CSV. L'URL d'objet est révoquée aussitôt le clic traité. */
function telecharger(organisations: DonneesPlateforme['organisations'], maintenant: Date) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([construireCsv(organisations, maintenant)], { type: 'text/csv;charset=utf-8' }))
  a.download = 'plateforme-tenants.csv'
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
}

export function TableOrgs({
  donnees,
  surOuvrir,
  surNouveau,
  surExport,
}: {
  donnees: DonneesPlateforme
  surOuvrir: (orgId: string) => void
  surNouveau: () => void
  surExport: () => void
}) {
  const { organisations, chargeLe } = donnees
  const operatrices = organisations.filter((o) => o.is_operator).length
  return (
    <section className="carte">
      <div className="carte-tete">
        <div>
          <h2>Organisations</h2>
          <p className="sous">{organisations.length} organisation(s) · {operatrices} opératrice(s)</p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-plein btn-sm" onClick={surNouveau}>
            <Icone nom="plus" /> Nouveau client
          </button>
          <button
            type="button" className="btn btn-doux btn-sm"
            onClick={() => { telecharger(organisations, chargeLe); surExport() }}
          >
            <Icone nom="download" /> Export CSV
          </button>
        </div>
      </div>
      <div className="defile">
        <table>
          <thead>
            <tr>
              <th>Organisation</th><th>Palier</th><th>Abonnement</th><th>API</th>
              <th className="c">Membres</th><th className="c">Livreurs</th><th className="c">Véhicules</th>
              <th className="c">Courses 30 j</th><th className="d">Revenu cumulé</th>
              <th>Dernier paiement</th><th>Créée le</th><th><span className="sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {organisations.length === 0 ? (
              <tr><td colSpan={12}><div className="vide">Aucune organisation.</div></td></tr>
            ) : organisations.map((o) => {
              const nom = o.brand_name || o.name
              const st = statutDe(o, chargeLe)
              const diverge = palierDiverge(o, chargeLe)
              return (
                <tr key={o.org_id}>
                  <td>
                    <div className="org-nom">
                      <span className="avatar-org" style={{ background: couleurOrganisation(nom) }} aria-hidden="true">{initiales(nom)}</span>
                      <div>
                        <div className="fort">{nom}</div>
                        <div className="discret">
                          {o.is_operator ? 'Organisation opératrice' : o.is_white_label ? 'Marque blanche' : 'SaaS'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <BadgePalier o={o} />
                    {diverge && <div className="discret">appliqué : {diverge}</div>}
                  </td>
                  <td>
                    <Badge classe={st.classe}>{st.libelle}</Badge>
                    {!o.is_operator && o.period_end && (
                      <div className="discret">jusqu'au {dateLongue(o.period_end)}</div>
                    )}
                  </td>
                  <td>
                    {o.acces_api
                      ? <Badge classe={o.is_operator ? 'gris' : classeAccesApi(o.acces_api)}>{libelleAccesApi(o.acces_api)}</Badge>
                      : null}
                  </td>
                  <td className="c n">{nombre(o.members)}</td>
                  <td className="c n">{nombre(o.drivers)}</td>
                  <td className="c n">{nombre(o.vehicles)}</td>
                  <td className="c n">{nombre(o.courses_30d)}</td>
                  <td className="d n fort">{fcfa(o.revenue_fcfa)}</td>
                  <td className="discret-t">{dateLongue(o.last_payment)}</td>
                  <td className="discret-t">{dateLongue(o.created_at)}</td>
                  <td>
                    <button type="button" className="btn btn-doux btn-sm" onClick={() => surOuvrir(o.org_id)} aria-label={`Ouvrir la fiche de ${nom}`}>
                      Fiche
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="note">
        La console lit des <b>agrégats uniquement</b> : elle ne peut lire aucune donnée opérationnelle d'un tenant
        (courses, clients finaux, messages). Elle n'écrit que trois choses : <b>créer un nouveau client</b>,
        <b> activer ou modifier un abonnement</b> et <b>ouvrir ou fermer l'accès à l'API</b> (test, production) —
        ces deux derniers actes sont journalisés (qui, quand, avant, après, raison). L'onglet <b>Clients API</b> ajoute
        la création d'un client API (compte développeur) et l'ouverture de son accès, avec le même journal.
      </p>
    </section>
  )
}
